import { Group, Matrix4, Raycaster, Vector2, Vector3 } from 'three';

const _pointer = new Vector2();
const _event = { type: '', data: _pointer };

class InteractiveGroup extends Group {
  private isPointerOver = false;

  private targetObject;

  constructor(renderer, camera, context) {
    super();
    const debug = false;
    let arrowHelper1;
    let arrowHelper2;
    if (context && debug) {
      const dir = new Vector3(1, 0, 0);
      dir.normalize();
      const origin = new Vector3(0, 0, 0);
      const length = 1;
      const hex = 0xffff00;
      arrowHelper1 = new context.three.ArrowHelper(dir, origin, length, hex);
      arrowHelper2 = new context.three.ArrowHelper(dir, origin, length, hex);
      context.scene.add(arrowHelper1);
      context.scene.add(arrowHelper2);
    }

    const raycasterL = new Raycaster();
    const raycasterR = new Raycaster();
    const tempMatrix = new Matrix4();

    const element = renderer.domElement;

    const onPointerEvent = (event) => {
      event.stopPropagation();
      const rect = renderer.domElement.getBoundingClientRect();

      _pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      _pointer.y = (-(event.clientY - rect.top) / rect.height) * 2 + 1;

      raycasterL.setFromCamera(_pointer, camera);
      raycasterR.setFromCamera(_pointer, camera);

      const intersectsL = raycasterL.intersectObjects(this.children, false);
      const intersectsR = raycasterR.intersectObjects(this.children, false);

      if (!intersectsL.length && this.isPointerOver && this.targetObject) {
        this.isPointerOver = false;
        this.targetObject.userData.isPointerOver = this.isPointerOver;
      }

      if (!intersectsR.length && this.isPointerOver && this.targetObject) {
        this.isPointerOver = false;
        this.targetObject.userData.isPointerOver = this.isPointerOver;
      }

      if (intersectsL.length > 0) {
        const intersection = intersectsL[0];

        this.targetObject = intersection.object;
        const { uv } = intersection;

        _event.type = event.type;
        _event.data.set(uv.x, 1 - uv.y);

        if (!this.isPointerOver) {
          this.isPointerOver = true;
          this.targetObject.userData.isPointerOver = this.isPointerOver;
        }

        this.targetObject.dispatchEvent(_event);
      }
      if (intersectsR.length > 0) {
        const intersection = intersectsR[0];

        this.targetObject = intersection.object;
        const { uv } = intersection;

        _event.type = event.type;
        _event.data.set(uv.x, 1 - uv.y);

        if (!this.isPointerOver) {
          this.isPointerOver = true;
          this.targetObject.userData.isPointerOver = this.isPointerOver;
        }

        this.targetObject.dispatchEvent(_event);
      }
    };

    element.addEventListener('pointerdown', onPointerEvent);
    element.addEventListener('pointerup', onPointerEvent);
    element.addEventListener('pointermove', onPointerEvent);
    element.addEventListener('mousedown', onPointerEvent);
    element.addEventListener('mouseup', onPointerEvent);
    element.addEventListener('mousemove', onPointerEvent);
    element.addEventListener('click', onPointerEvent);

    // WebXR Controller Events
    // TODO: Dispatch pointerevents too

    const events = {
      move: 'mousemove',
      select: 'click',
      selectstart: 'mousedown',
      selectend: 'mouseup',
    };

    const controller1 = renderer.xr.getController(0);
    const controller2 = renderer.xr.getController(1);

    // Track handedness dynamically to avoid index-based mismatches across sessions
    let leftController: any = null;
    let rightController: any = null;

    // Only one active controller should drive interactions at a time
    let activeController: any = null;

    const setHandedness = (controller, e) => {
      const handedness = e?.data?.handedness;
      if (handedness === 'left') leftController = controller;
      else if (handedness === 'right') rightController = controller;
    };

    controller1.addEventListener('connected', (e) => setHandedness(controller1, e));
    controller2.addEventListener('connected', (e) => setHandedness(controller2, e));
    controller1.addEventListener('disconnected', () => { if (activeController === controller1) activeController = null; });
    controller2.addEventListener('disconnected', () => { if (activeController === controller2) activeController = null; });

    // Reset active controller across VR session boundaries
    renderer.xr.addEventListener('sessionstart', () => { activeController = null; });
    renderer.xr.addEventListener('sessionend', () => { activeController = null; });

    const visualiseRaycaster = (arrowHelper, raycaster) => {
      if (!debug) return;
      arrowHelper.setDirection(raycaster.ray.direction);
      arrowHelper.position.set(
        raycaster.ray.origin.x,
        raycaster.ray.origin.y,
        raycaster.ray.origin.z,
      );
    };
    const onXRControllerEvent = (event) => {
      const controller = event.target;

      // Wait until handedness is known to avoid index-based mismatches
      const handednessReady = !!(leftController || rightController);
      if (!handednessReady) return;

      // Lock active controller on selectstart only. Do not switch on move.
      if (!activeController) {
        if (event.type !== 'selectstart') return; // ignore move/select until user picks a hand
        activeController = controller;
      } else if (controller !== activeController) {
        // Allow explicit hand switch only when the other controller initiates a selectstart
        if (event.type !== 'selectstart') return;
        activeController = controller;
      }

      let raycaster;
      let arrowHelper;

      // Use handedness mapping only; if we cannot identify, ignore to avoid swaps
      if (controller === leftController) {
        raycaster = raycasterL;
        arrowHelper = arrowHelper2;
      } else if (controller === rightController) {
        raycaster = raycasterR;
        arrowHelper = arrowHelper1;
      } else {
        return; // unknown controller mapping
      }

      tempMatrix.identity().extractRotation(controller.matrixWorld);

      raycaster.ray.origin.setFromMatrixPosition(controller.matrixWorld);
      raycaster.ray.direction.set(0, 0, -1).applyMatrix4(tempMatrix);

      if (arrowHelper) visualiseRaycaster(arrowHelper, raycaster);

      const intersections = raycaster.intersectObjects(this.children, false);

      if (intersections.length > 0) {
        const intersection = intersections[0];

        const { object } = intersection;
        const { uv } = intersection;

        _event.type = events[event.type];
        _event.data.set(uv.x, 1 - uv.y);

        object.dispatchEvent(_event);
      }
    };

    controller1.addEventListener('move', onXRControllerEvent);
    controller1.addEventListener('select', onXRControllerEvent);
    controller1.addEventListener('selectstart', onXRControllerEvent);
    controller1.addEventListener('selectend', onXRControllerEvent);
    controller2.addEventListener('move', onXRControllerEvent);
    controller2.addEventListener('select', onXRControllerEvent);
    controller2.addEventListener('selectstart', onXRControllerEvent);
    controller2.addEventListener('selectend', onXRControllerEvent);
  }
}

export { InteractiveGroup };
